/**
 * threadScrollRestore — persisted per-thread view markers
 * (returning-tab reliability, 2026-09-01).
 *
 * The shared scroll controller (useTranscriptScroll) deliberately keeps its
 * per-thread reading positions IN MEMORY only — the old cross-surface
 * localStorage blob (GLOBAL_CHAT_SCROLL_KEY, retired) cross-clobbered
 * restores. This module is the sanctioned persistence layer that replaces a
 * raw-pixel blob with a LAYOUT-ROBUST marker: the canonical id of the
 * topmost visible message (perDocMessageId — survives the UUID↔task-anchor
 * id convergence across reloads) plus neighbor fallbacks and a pixel offset,
 * namespaced per uid+instance+thread. GlobalChatSystem owns the writer and
 * the restore sequencing; the scroll CONTROLLER stays storage-free (the
 * threadScrollingUx pins).
 *
 * Follows the pendingThreadOpen idiom (module-local KEY, try/catch every
 * storage access) with one deliberate difference: reads are NON-destructive
 * (peek, not consume) — StrictMode double-mounts and visibility-wake
 * double-restores re-read the same marker, and restore itself is idempotent.
 *
 * Multi-tab: the whole map lives under one key, read-modify-write,
 * last-writer-wins. A lost update can drop another tab's most recent marker
 * — accepted; writes are debounced and pinned-to-bottom CLEARS, keeping the
 * window small.
 */

export interface ThreadViewMarker {
  /** Canonical perDocMessageId of the topmost visible message. */
  anchorMessageId: string;
  /** Canonical ids of the next messages down — fallback when the anchor was pruned or renders no element. */
  neighborIds: string[];
  /** The anchor's offset from the scroller's top edge at save time (px). */
  offsetPx: number;
  /** Whether the reveal-older container was expanded ("previous N" state). */
  revealAll: boolean;
  savedAt: number;
}

// One JSON map under one key — NEVER the retired global scroll blob.
const KEY = 'prest0_thread_view_v1';

// Long TTL on purpose: "idle for months" is the use case. A marker that
// outlives its thread's local history degrades to the bottom default at
// restore time — persistence never depends on anything but message ids.
export const THREAD_VIEW_TTL_MS = 90 * 24 * 60 * 60 * 1000; // ~90 days
// LRU by savedAt — same cap as the controller's in-memory map.
export const THREAD_VIEW_CAP = 50;

/** uid+instance+thread namespacing — guests share the 'guest' segment. */
export function buildThreadViewKey(
  uid: string | null | undefined,
  instanceId: string,
  threadId: string,
): string {
  return `${(uid || 'guest').trim()}|${instanceId}|${threadId}`;
}

type MarkerMap = Record<string, ThreadViewMarker>;

/** Field-by-field shape validation (consumePendingThreadOpen discipline). */
function isValidMarker(value: unknown, now: number): value is ThreadViewMarker {
  if (!value || typeof value !== 'object') return false;
  const m = value as Partial<ThreadViewMarker>;
  if (typeof m.anchorMessageId !== 'string' || !m.anchorMessageId) return false;
  if (!Array.isArray(m.neighborIds) || m.neighborIds.some((n) => typeof n !== 'string')) return false;
  if (typeof m.offsetPx !== 'number' || !Number.isFinite(m.offsetPx)) return false;
  if (typeof m.revealAll !== 'boolean') return false;
  if (typeof m.savedAt !== 'number' || !Number.isFinite(m.savedAt)) return false;
  if (now - m.savedAt > THREAD_VIEW_TTL_MS) return false; // expired
  return true;
}

/**
 * Read the map, dropping corrupt/expired entries as it validates. Corrupt
 * JSON self-heals to an empty map (the next save replaces the blob — lazy
 * prune). Returns null ONLY when storage itself is unusable; callers treat
 * that as "no marker" / silent no-op and never throw.
 */
function readMap(now: number): MarkerMap | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null; // storage unavailable (blocked context) — fail closed, silent
  }
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: MarkerMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (isValidMarker(v, now)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: MarkerMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch { /* quota / storage unavailable — restore is a convenience, never a failure */ }
}

/**
 * Save one marker: read-modify-write (which also drops expired/corrupt
 * entries — the lazy prune), oldest-savedAt evicted past the cap.
 */
export function saveThreadViewMarker(entryKey: string, marker: ThreadViewMarker): void {
  const map = readMap(Date.now());
  if (map === null) return;
  map[entryKey] = marker;
  const keys = Object.keys(map);
  if (keys.length > THREAD_VIEW_CAP) {
    keys
      .sort((a, b) => map[a].savedAt - map[b].savedAt)
      .slice(0, keys.length - THREAD_VIEW_CAP)
      .forEach((k) => { delete map[k]; });
  }
  writeMap(map);
}

/** Remove one entry only (pinned-to-bottom = clear: bottom stays the default). */
export function clearThreadViewMarker(entryKey: string): void {
  const map = readMap(Date.now());
  if (map === null || !(entryKey in map)) return;
  delete map[entryKey];
  writeMap(map);
}

/** NON-destructive read: expired or corrupt → null; never throws. */
export function peekThreadViewMarker(entryKey: string, now = Date.now()): ThreadViewMarker | null {
  const map = readMap(now);
  if (map === null) return null;
  return map[entryKey] ?? null;
}

/**
 * Drop every marker namespaced to one uid — the sign-out/uid-switch purge
 * (rides the same session-reset sweep that clears the thread namespaces;
 * markers hold message ids only, but a departing identity leaves nothing
 * behind). Guests ride the 'guest' segment and are NOT purged here — guest
 * history deliberately survives sign-in/out (guest-history protection).
 */
export function clearThreadViewMarkersForUid(uid: string): void {
  const trimmed = (uid || '').trim();
  if (!trimmed || trimmed === 'guest') return;
  const map = readMap(Date.now());
  if (map === null) return;
  const prefix = `${trimmed}|`;
  let changed = false;
  for (const k of Object.keys(map)) {
    if (k.startsWith(prefix)) {
      delete map[k];
      changed = true;
    }
  }
  if (changed) writeMap(map);
}
